import type { KeyboardEvent } from 'react';

export type AxisCardOption<T extends string> = {
  id: T;
  title: string;
  description: string;
  enabled?: boolean;
  reason?: string;
};

export function CampaignWizardAxisCards<T extends string>({
  legend,
  value,
  options,
  onChange,
}: {
  legend: string;
  value: T;
  options: Array<AxisCardOption<T>>;
  onChange: (id: T) => void;
}) {
  const enabledIds = options.filter(option => option.enabled !== false).map(option => option.id);

  const move = (current: T, delta: number) => {
    const index = enabledIds.indexOf(current);
    if (index < 0) {
      onChange(enabledIds[0]);
      return;
    }
    const next = enabledIds[(index + delta + enabledIds.length) % enabledIds.length];
    if (next) {
      onChange(next);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, option: AxisCardOption<T>) => {
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      move(value, 1);
      return;
    }
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      move(value, -1);
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (option.enabled !== false) {
        onChange(option.id);
      }
    }
  };

  return (
    <fieldset className='md:col-span-2 space-y-4'>
      <legend className='text-sm font-medium'>{legend}</legend>
      <div className={`grid gap-4 ${options.length === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
        {options.map(option => {
          const selected = value === option.id;
          const enabled = option.enabled !== false;
          return (
            <button
              key={option.id}
              type='button'
              disabled={!enabled}
              aria-pressed={selected}
              aria-disabled={!enabled}
              title={option.reason}
              onClick={() => enabled && onChange(option.id)}
              onKeyDown={event => onKeyDown(event, option)}
              className={`rounded-xl border p-6 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                selected ? 'border-primary ring-2 ring-primary' : 'border-border'
              } ${enabled ? '' : 'cursor-not-allowed opacity-60'}`}
            >
              <p className='text-lg font-medium tracking-tight'>{option.title}</p>
              <p className='mt-1 text-sm text-muted-foreground'>{option.description}</p>
              {!enabled && option.reason ? (
                <p className='mt-3 text-xs text-muted-foreground'>{option.reason}</p>
              ) : null}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}
